import { Location } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { cpf } from "cpf-cnpj-validator";
import { Empresa, Representantes } from "src/app/auth/models/empresa.model";
import { IUpdateEmpresas } from "src/app/auth/models/register.model";
import { CadastroService } from "src/app/auth/services/cadastro.service";
import { encryptStorage } from "src/app/auth/utils/storage";
import { LogStorageService } from "src/app/shared/services/log-storage.service";
import { showError, showSucess } from "src/app/shared/utils/functions/notification.function";
import { endpoints } from "src/environments/endpoints";
import { PerfilService } from "../../services/perfil.service";
import { emailValidator } from "src/app/shared/utils/validators/forms.validators";

@Component({
  selector: "app-editar-representante",
  templateUrl: "./editar-representante.component.html",
  styleUrls: ["./editar-representante.component.scss"],
})
export class EditarRepresentanteComponent implements OnInit {
  empresa: Empresa;
  formEmpresa: FormGroup;
  idPedido: string;
  editaRepresentantes: any;


  readonly defaultEmpresaVazia = {
    cpf: null,
    nome: null,
    emailPj: null,
  };

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private perfilService: PerfilService,
    private cadastroService: CadastroService,
    private logStorageService: LogStorageService
  ) {}

  ngOnInit(): void {
    this.idPedido = this.route.snapshot.paramMap.get("id");
    const cnpjEmpresa = this.route.snapshot.paramMap.get("cnpj");
    const cpfResponsavel = encryptStorage.getItem("@documentoIdentificador");

    if (!cnpjEmpresa || !cpfResponsavel) {
      showError("", "Nenhuma empresa selecionada para edição.");
      this.voltar();
      return;
    }

    this.perfilService.listarEmpresa(cnpjEmpresa, cpfResponsavel).subscribe(
      (res) => {
        this.empresa = res["data"] as Empresa;
        this.editaRepresentantes = res.data?.editaRepresentantes ?? false;
        this.createFormEmpresa(this.empresa);
      },
      () => {
        showError("", "Erro ao buscar dados da empresa.");
        this.voltar();
      }
    );
  }

  updateEmpresa() {
    if (!this.validateFormEmpresa()) {
      return;
    }

    const _formValue = this.formEmpresa.getRawValue();
    const _representantes = _formValue?.representantes?.filter((rep) => rep.nome && rep.cpf && rep.emailPj) || [];
      const nomeEmpresa = _formValue?.nomeEmpresa || "";
    const body: IUpdateEmpresas = {
      cnpj: this.empresa.cnpj,
      nomeEmpresa: nomeEmpresa,
      representantes: _representantes,
      idPedido: this.idPedido,
    };

    this.logStorageService.logRequest(
      "Meu Perfil",
      "Editar Credor Pessoa Juridica",
      endpoints.atualizarDadosEmpresa,
      body
    );

    this.cadastroService.editarDadosEmpresa(body).subscribe({
      next: () => {
        this.logStorageService.logResponse(
          "Meu Perfil",
          "Editar Credor Pessoa Juridica",
          endpoints.atualizarDadosEmpresa,
          { success: true }
        );
        showSucess("", "Empresa atualizada com sucesso!");
        this.voltar();
      },
      error: (error) => {
        showError("", error.error?.message || "Ocorreu um erro, tente novamente.");
      },
    });
  }

  get representantes(): FormArray {
    return this.formEmpresa.get("representantes") as FormArray;
  }

  voltar() {
    this.location.back();
  }

  private validateFormEmpresa(): boolean {
    const empresa = this.formEmpresa.value;
    if (!empresa.nomeEmpresa || empresa.nomeEmpresa.trim().length === 0) {
      showError("", "O campo Razão Social é obrigatório.");
      return false;
    }

    if (this.formEmpresa.get("nomeEmpresa")?.hasError("pattern")) {
      showError("", "O campo Razão Social contém caracteres inválidos.");
      return false;
    }

    const cpfs: string[] = [];
    const nomes: string[] = [];
    const emails: string[] = [];

    for (let index = 0; index < this.representantes.length; index++) {
      const representante = this.representantes.at(index).value;

      if (index > 0 && !representante.nome && !representante.cpf && !representante.emailPj) {
        continue;
      }

      if (!representante.nome || !representante.cpf || !representante.emailPj) {
        showError(
          "",
          index === 0
            ? `Todos os campos do representante ${index + 1} são obrigatórios.`
            : `Preencha todos os campos do representante ${index + 1}, ou deixe todos em branco.`
        );
        return false;
      }

      const emailRegex = /^[A-Za-z0-9._+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
      if (!emailRegex.test(representante.emailPj)) {
        showError("", `E-mail do representante ${index + 1} é inválido.`);
        return false;
      }
      if (!cpf.isValid(representante.cpf)) {
        showError("", `CPF do representante ${index + 1} é inválido.`);
        return false;
      }
      if (cpfs.includes(representante.cpf)) {
        showError("", `CPF do representante ${index + 1} está duplicado.`);
        return false;
      }
      if (nomes.includes(representante.nome)) {
        showError("", `Nome do representante ${index + 1} está duplicado.`);
        return false;
      }
      if (emails.includes(representante.emailPj)) {
        showError("", `Email do representante ${index + 1} está duplicado.`);
        return false;
      }

      cpfs.push(representante.cpf);
      nomes.push(representante.nome);
      emails.push(representante.emailPj);
    }

    return true;
  }

  private createFormEmpresa(value: Empresa) {
    const representantes = new FormArray([]);
    for (let index = 0; index < 2; index++) {
      const representante = value?.representantes[index] || this.defaultEmpresaVazia;
      representantes.push(this.createFormRepresentante(representante, index === 0));
    }

    this.formEmpresa = new FormGroup({
      cnpj: new FormControl({ value: value.cnpj, disabled: true }, [Validators.required]),
      nomeEmpresa: new FormControl({value: value.nomeEmpresa || "", disabled: !this.editaRepresentantes}, [
        Validators.required,
        Validators.pattern(/^[^!"?\[\\\]‘’“”«»–.]*$/),
      ]),
      representantes,
    });
  }

  private createFormRepresentante(representante?: Representantes, required = true) {
    const validarNome = required ? [Validators.required, Validators.minLength(3)] : [Validators.minLength(3)];
    const validarCpf = required ? [Validators.required, Validators.minLength(11), Validators.maxLength(14)] : [Validators.minLength(11), Validators.maxLength(14)];
    const validarEmail = required ? [Validators.required, Validators.maxLength(50), emailValidator] : [Validators.maxLength(50), emailValidator];

    return new FormGroup({
      nome: new FormControl({value: representante?.nome || '', disabled: !this.editaRepresentantes}, validarNome),
      cpf: new FormControl({value: representante?.cpf || "", disabled: !this.editaRepresentantes}, validarCpf),
      emailPj: new FormControl({value: representante?.emailPj || "", disabled: !this.editaRepresentantes}, validarEmail),
    });
  }
}
